import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Card,
    CardContent,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Grid,
    Box,
    TextField,
    Alert,
    Divider
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { Notyf } from 'notyf';
import 'notyf/notyf.min.css';

// --- Initialize Notyf for notifications ---
const notyf = new Notyf({
    position: { x: 'center', y: 'top' },
    duration: 4000,
});

// --- Constants ---
const plans = {
    free: {
        monthly: 0,
        yearly: 0,
        features: [
            'Create Invoice',
            'Invoices Report',
            'Add Customers',
            'Add Products',
        ]
    },
    basic: {
        monthly: 1000.00,
        yearly: 10800.20,
        features: [
            'Create Invoice',
            'Add Expenses',
            'Manage Inventory',
            'And More',
        ]
    },
    premium: {
        monthly: 2500.00,
        yearly: 27000.20,
        features: ['All Basic Features', 'Manage Inventory', 'Return Management', '24/7 support']
    }
};

const PAYHERE_MERCHANT_ID = "243166";
const PAYHERE_LIVE_URL = "https://www.payhere.lk/pay/checkout";
const PAYHERE_NOTIFY_URL = "https://admin.bizstor.lk/public/api/payment-notification"; // Your backend notification URL
const API_BASE_URL = "https://admin.bizstor.lk/public/api"; // Your API base URL

// --- Main Component ---
export default function SubscriptionUpgradePage() {
    // --- State Management ---
    const [selectedPlan, setSelectedPlan] = useState('free');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [paymentData, setPaymentData] = useState({
        merchant_id: PAYHERE_MERCHANT_ID || '',
        subscription_id: `SUB_${Math.random().toString(36).substr(2, 9)}`,
        amount: 0,
        currency: 'LKR',
        subscription_period: 'monthly',
        user_email: '',
        user_phone: '',
        first_name: '',
        last_name: '',
    });
    const [user, setUser] = useState({});
    const [errors, setErrors] = useState({});
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false); // State for the thank you page
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [isPayHereLoaded, setIsPayHereLoaded] = useState(false);
    const [token] = useState(() => localStorage.getItem('token') || null);
    const navigate = useNavigate();

    // --- Effects ---

    // Effect to fetch user profile and pre-fill data
    useEffect(() => {
        const fetchProfile = async () => {
            if (!token) {
                notyf.error("Authentication error. Please log in again.");
                navigate("/");
                return;
            }
            try {
                const response = await axios.get(`${API_BASE_URL}/profile`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const userData = response.data;
                setUser(userData);
                // Pre-fill payment data with fetched user info
                setPaymentData(prev => ({
                    ...prev,
                    first_name: userData.name || '',
                    last_name: userData.lastName || '',
                    user_email: userData.email || '',
                    user_phone: userData.phone || '',
                }));
            } catch (error) {
                console.error("Failed to fetch profile:", error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };
        fetchProfile();
    }, [navigate, token]);

    // Effect to load PayHere script and set up handlers
    useEffect(() => {
        const setupPayHereEventHandlers = () => {
            if (!window.payhere) {
                console.error('PayHere object not available for setup');
                return;
            }
            // These are default handlers. They will be reassigned with current data
            // right before starting a payment to avoid stale state issues.
            window.payhere.onCompleted = (orderId) => {
                //console.log(`Payment completed with ID: ${orderId}`);
                saveSubscriptionToDatabase(orderId, paymentData);
            };
            window.payhere.onDismissed = () => {
                setPaymentProcessing(false);
                setSubmissionStatus('cancelled');
                notyf.error('Payment was cancelled.');
            };
            window.payhere.onError = (error) => {
                console.error("PayHere Error:", error);
                setPaymentProcessing(false);
                setSubmissionStatus('error');
                notyf.error('Payment failed. Please try again.');
            };
        };

        if (!window.payhere) {
            const script = document.createElement('script');
            script.src = 'https://www.payhere.lk/lib/payhere.js';
            script.async = true;
            script.onload = () => {
                setIsPayHereLoaded(true);
                setupPayHereEventHandlers();
            };
            script.onerror = (error) => {
                console.error('Failed to load PayHere script:', error);
                notyf.error('Payment gateway could not be loaded. Please refresh.');
            };
            document.body.appendChild(script);
        } else {
            setIsPayHereLoaded(true);
            setupPayHereEventHandlers();
        }
    }, [paymentData]); // Rerun if paymentData changes to keep handlers fresh

    // Effect to update payment amount when plan or billing cycle changes
    useEffect(() => {
        const amount = billingCycle === 'yearly'
            ? plans[selectedPlan].yearly
            : plans[selectedPlan].monthly;

        setPaymentData(prev => ({
            ...prev,
            amount,
            subscription_period: billingCycle,
        }));
    }, [selectedPlan, billingCycle]);


    const saveSubscriptionToDatabase = async (orderId, currentPaymentData) => {
        try {
            const subscriptionData = {
                email: currentPaymentData.user_email || paymentData.user_email || user.email,
                plan: selectedPlan,
                billing_cycle: billingCycle,
                subscription_id: currentPaymentData.subscription_id || paymentData.subscription_id,
                amount: currentPaymentData.amount || paymentData.amount,
                order_id: orderId,
                payment_status: 'completed',
                user_phone: currentPaymentData.user_phone || paymentData.user_phone || user.phone,
                first_name: currentPaymentData.first_name || paymentData.first_name || user.name,
                last_name: currentPaymentData.last_name || paymentData.last_name || user.lastName
            };


            if (!subscriptionData.email) {
                throw new Error('Email is required but missing');
            }

            const response = await fetch('https://admin.bizstor.lk/public/api/save-subscription', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(subscriptionData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `Server error: ${response.status}`);
            }

            notyf.success('Subscription activated successfully!');

            setIsCompleted(true);

        } catch (error) {
            console.error('Failed to save subscription:', error);
            notyf.error('There was an issue saving your subscription. Please contact support with your order ID: ' + orderId);
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handlePaymentConfirmation = async () => {
        setPaymentProcessing(true);
        try {
            const orderResponse = await fetch(`${API_BASE_URL}/generate-order-id`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subscription_id: paymentData.subscription_id,
                    plan: selectedPlan,
                    amount: paymentData.amount
                }),
            });
            const orderData = await orderResponse.json();
            if (!orderResponse.ok) throw new Error(orderData.error || 'Failed to get order ID');

            const orderId = orderData.order_id;

            // Re-assign handlers right before payment to use the latest state in a closure
            const currentPaymentDataForCallback = { ...paymentData };
            window.payhere.onCompleted = (completedOrderId) => {
                console.log('Payment completed callback fired for Order ID:', completedOrderId);
                saveSubscriptionToDatabase(completedOrderId, currentPaymentDataForCallback);
            };

            const payment = {
                sandbox: false,
                merchant_id: PAYHERE_MERCHANT_ID,
                return_url: window.location.href,
                cancel_url: window.location.href,
                notify_url: PAYHERE_NOTIFY_URL,
                order_id: orderId,
                items: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan (${billingCycle})`,
                amount: paymentData.amount.toString(),
                currency: paymentData.currency,
                hash: orderData.hash,
                first_name: paymentData.first_name,
                last_name: paymentData.last_name,
                email: paymentData.user_email,
                phone: paymentData.user_phone,
                address: "N/A", city: "Colombo", country: "Sri Lanka",
            };

            if (window.payhere && typeof window.payhere.startPayment === 'function') {
                window.payhere.startPayment(payment);
            } else {
                throw new Error('PayHere is not properly loaded');
            }

        } catch (error) {
            console.error('Payment initiation failed:', error);
            setSubmissionStatus('error');
            setPaymentProcessing(false);
            notyf.error('Payment initiation failed: ' + error.message);
        }
    };

    const handleFreePlanActivation = async () => {
        setPaymentProcessing(true);
        const fakeOrderId = `FREE-${Date.now()}`;
        await saveSubscriptionToDatabase(fakeOrderId, paymentData);
    };

    // --- UI Handlers ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPaymentData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handlePlanChange = (event) => setSelectedPlan(event.target.value);
    const handleBillingChange = (event) => setBillingCycle(event.target.value);

    const handleSubmit = () => {
        const newErrors = {};
        if (!paymentData.first_name) newErrors.first_name = 'First name is required';
        if (!paymentData.last_name) newErrors.last_name = 'Last name is required';
        if (!paymentData.user_email) newErrors.user_email = 'Email is required';
        if (!paymentData.user_phone) newErrors.user_phone = 'Phone is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        if (selectedPlan !== 'free' && !isPayHereLoaded) {
            notyf.error('Payment gateway is not ready. Please wait or refresh the page.');
            return;
        }

        if (selectedPlan === 'free') {
            handleFreePlanActivation();
        } else {
            handlePaymentConfirmation();
        }
    };

    return (
        <Box sx={{ width: '90%', backgroundColor: '#FAFAFA', minHeight: '100vh',margin: 'auto', py: { xs: 2, md: 5 },}}>
            {isCompleted ? (
                <Box sx={{ maxWidth: 600, margin: 'auto', p: 4, textAlign: 'center', borderRadius: 4, bgcolor: 'FAFAFA',mt:20 }}>
                    <Typography variant="h4" fontWeight={'bold'} color="primary.main" gutterBottom>
                         Subscription Updated!
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                        Your account is now on the {selectedPlan} plan ({billingCycle}).
                    </Typography>
                    <Button variant="contained" size='small' sx={{ mt: 4, borderRadius: '8px', px: 4, py: 1.5 }} onClick={() => navigate('/dashboard')}>
                        Back to Dashboard
                    </Button>
                </Box>
            ) : (
                <Box sx={{ width: '100%', maxWidth: '1200px', margin: 'auto',mt:{sm:8,xs:9} }}>
                    <Box textAlign="center" mb={5}>
                        <Typography variant="h4" gutterBottom fontWeight="bold" color="red.900">
                            Upgrade Your Plan
                        </Typography>
                        <Typography variant="body1" color="red.800">
                            Choose a new plan to unlock more features.
                        </Typography>
                    </Box>

                    <Grid container spacing={{ xs: 4, md: 5 }}>
                        {/* --- Left Column: Plans & Details --- */}
                        <Grid item xs={12} md={7}>
                            <Box component={Card} variant="outlined" p={3} borderRadius={4} mb={4}>
                                <Typography variant="h6" fontWeight="bold" mb={2}>1. Choose Your Plan</Typography>
                                <FormControl component="fieldset" fullWidth>
                                    <FormLabel component="legend">Billing Cycle</FormLabel>
                                    <RadioGroup row value={billingCycle} onChange={handleBillingChange}>
                                        <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                                        <FormControlLabel value="yearly" control={<Radio />} label="Yearly (Save 10%)" />
                                    </RadioGroup>
                                </FormControl>
                                <Grid container spacing={2} mt={1}>
                                    <Grid item xs={12} sm={4}><PlanCard name="Free" value="free" selected={selectedPlan === 'free'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                                    <Grid item xs={12} sm={4}><PlanCard name="Basic" value="basic" selected={selectedPlan === 'basic'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                                    <Grid item xs={12} sm={4}><PlanCard name="Premium" value="premium" selected={selectedPlan === 'premium'} onChange={handlePlanChange} billingCycle={billingCycle} /></Grid>
                                </Grid>
                            </Box>

                            <Box component={Card} variant="outlined" p={3} borderRadius={4}>
                                <Typography variant="h6" fontWeight="bold" mb={2}>2. Billing Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}><TextField size='small' fullWidth label="First Name" name="first_name" value={paymentData.first_name || ''} onChange={handleInputChange} error={!!errors.first_name} helperText={errors.first_name} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField size='small' fullWidth label="Last Name" name="last_name" value={paymentData.last_name || ''} onChange={handleInputChange} error={!!errors.last_name} helperText={errors.last_name} /></Grid>
                                    <Grid item xs={12}><TextField size='small' fullWidth label="Email" name="user_email" type="email" value={paymentData.user_email || ''} onChange={handleInputChange} error={!!errors.user_email} helperText={errors.user_email} disabled={!!user.email} /></Grid>
                                    <Grid item xs={12}><TextField size='small' fullWidth label="Phone Number" name="user_phone" value={paymentData.user_phone || ''} onChange={handleInputChange} error={!!errors.user_phone} helperText={errors.user_phone} /></Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* --- Right Column: Summary --- */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ position: 'sticky', top: 80 }}>
                                <Card variant="outlined" sx={{ p: 3, borderRadius: 4 }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>Order Summary</Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Box display="flex" justifyContent="space-between" mb={1}><Typography color="text.secondary">Plan:</Typography><Typography fontWeight="medium">{selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}</Typography></Box>
                                    <Box display="flex" justifyContent="space-between" mb={2}><Typography color="text.secondary">Billing:</Typography><Typography fontWeight="medium">{billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)}</Typography></Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box display="flex" justifyContent="space-between" alignItems="baseline">
                                        <Typography variant="h6" fontWeight="bold">Total</Typography>
                                        <Box textAlign="right">
                                            <Typography variant="h4" fontWeight="bold" color="primary">LKR {paymentData.amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography>
                                            <Typography color="text.secondary">per {billingCycle === 'yearly' ? 'year' : 'month'}</Typography>
                                        </Box>
                                    </Box>
                                    <Button fullWidth variant="contained" size="large" onClick={handleSubmit} disabled={paymentProcessing}
                                        sx={{ mt: 3, py: 1.5, borderRadius: '8px', fontWeight: 'bold', bgcolor: 'rgba(0, 0, 0, 0.93)', '&:hover': { bgcolor: 'rgba(0, 0, 0, 1)' } }}>
                                        {paymentProcessing ? <CircularProgress size={26} color="inherit" /> : (selectedPlan === 'free' ? 'Switch to Free Plan' : 'Upgrade Plan')}
                                    </Button>
                                    <Alert severity="info" sx={{ mt: 3 }}>
                                        <strong>Note:</strong> This is a one-time payment. You will need to manually renew your subscription when it expires.
                                    </Alert>
                                </Card>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

// --- Reusable PlanCard Component ---
function PlanCard({ name, value, selected, onChange, billingCycle }) {
    const plan = plans[value.toLowerCase()];
    const price = billingCycle === 'yearly' ? plan.yearly : plan.monthly;

    return (
        <Card
            onClick={() => onChange({ target: { value } })}
            variant="outlined"
            sx={{
                borderColor: selected ? 'primary.main' : 'grey.300',
                borderWidth: selected ? 2 : 1,
                height: '100%',
                cursor: 'pointer',
                borderRadius: '12px',
                position: 'relative',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.light',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }
            }}
        >
            <CardContent sx={{ textAlign: 'center' }}>
                <Radio checked={selected} value={value} name="plan-selection" sx={{ position: 'absolute', top: 8, right: 8 }} />
                <Typography variant="h6" fontWeight="bold">{name}</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main" my={1}>LKR {price.toFixed(2)}</Typography>
                <Typography color="text.secondary" variant="caption">per {billingCycle}</Typography>
                <Divider sx={{ my: 2 }} />
                <Box component="ul" sx={{ p: 0, listStyle: 'none', m: 0, textAlign: 'left' }}>
                    {plan.features.map((feature, index) => (
                        <Typography component="li" key={index} variant="body2" sx={{ mb: 0.5 }}>✓ {feature}</Typography>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
}